import { useCallback, useEffect, useState } from 'react';
import { User, UserGroup } from '../../../models';
import { apiClient, apiErrorMessage } from '../../../utils';
import { Button, Modal, Table, Typography, Select, Input, message } from 'antd';
import { ExclamationCircleOutlined, PlusOutlined, SearchOutlined, CloseOutlined } from '@ant-design/icons';

export function UserGroupMembersForm(props: { userGroup?: UserGroup }) {
  const { Text } = Typography;
  const [allUsers, setAllUsers] = useState<User[]>();
  const [groupMembers, setGroupMembers] = useState<User[]>();
  const [membersLoading, setMembersLoading] = useState(false);
  const [allUsersLoading, setAllUsersLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [searchText, setSearchText] = useState('');

  const loadGroupMembers = useCallback(() => {
    setMembersLoading(true);
    apiClient
      .get(`user-groups/${props.userGroup?.id}/users`)
      .then((response: any) => {
        setGroupMembers(response.data);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setMembersLoading(false);
      });
  }, [props.userGroup?.id]);

  useEffect(() => {
    if (props.userGroup?.id) {
      loadGroupMembers();
    }
  }, [loadGroupMembers, props.userGroup?.id]);

  const loadAllUsers = () => {
    if (allUsers) return; // Don't reload if already loaded

    setAllUsersLoading(true);
    apiClient
      .get('users')
      .then((response: any) => {
        setAllUsers(response.data);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setAllUsersLoading(false);
      });
  };

  const handleAddModalOpen = () => {
    setAddModalVisible(true);
    setSelectedUserIds([]);
    loadAllUsers();
  };

  const confirmDeleteMember = (userId: number) => {
    Modal.confirm({
      title: 'Üye sil',
      icon: <ExclamationCircleOutlined />,
      content: 'Kullanıcıyı gruptan silmek istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'Vazgeç',
      onOk: () => removeMember(userId),
    });
  };

  const removeMember = (userId: number) => {
    setDeleting(true);
    apiClient
      .delete(`user-groups/${props.userGroup?.id}/users/${userId}`)
      .then(() => {
        message.success('Üye başarıyla silindi');
        loadGroupMembers();
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setDeleting(false);
      });
  };

  const addMembers = () => {
    if (!selectedUserIds.length) return;

    setSubmitting(true);
    apiClient
      .post(`user-groups/user-assignments`, { userGroupIds: [props.userGroup?.id], userIds: selectedUserIds })
      .then(() => {
        message.success(`${selectedUserIds.length} üye başarıyla eklendi`);
        setAddModalVisible(false);
        setSelectedUserIds([]);
        loadGroupMembers();
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const tableColumns = [
    {
      title: 'İsim',
      dataIndex: 'fullName',
      key: 'fullName',
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      title: 'E-posta',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Departman',
      dataIndex: 'department',
      key: 'department',
    },
    {
      title: '',
      key: 'action',
      render: (_: any, record: User) => (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <CloseOutlined
            className="text-red-500 cursor-pointer hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => !deleting && confirmDeleteMember(record.id)}
            style={{ opacity: deleting ? 0.5 : 1 }}
            disabled={deleting}
          />
        </div>
      ),
    },
  ];

  const filteredMembers = groupMembers?.filter(
    member =>
      member.fullName.toLowerCase().includes(searchText.toLowerCase()) ||
      member.email.toLowerCase().includes(searchText.toLowerCase()) ||
      member.department?.toLowerCase().includes(searchText.toLowerCase()),
  );

  const availableUsers = allUsers?.filter((user: User) => !groupMembers?.some((member: User) => member.id === user.id));

  return (
    <div>
      <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between' }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAddModalOpen}>
          Üye ekle
        </Button>
        <div>
          <Input
            placeholder="Arama"
            prefix={<SearchOutlined />}
            onChange={e => setSearchText(e.target.value)}
            style={{ width: 250 }}
            allowClear
          />
        </div>
      </div>

      <Table columns={tableColumns} dataSource={filteredMembers} rowKey="id" loading={membersLoading} pagination={false} />

      <Modal
        title="Üye Ekle"
        visible={addModalVisible}
        onOk={addMembers}
        onCancel={() => {
          setAddModalVisible(false);
          setSelectedUserIds([]);
        }}
        okButtonProps={{ disabled: !selectedUserIds.length, loading: submitting }}
      >
        <Select
          mode="multiple"
          showSearch
          style={{ width: '100%' }}
          placeholder="Kullanıcı seçin"
          onChange={value => setSelectedUserIds(value)}
          value={selectedUserIds}
          loading={allUsersLoading}
          filterOption={(input, option) => {
            const user = availableUsers?.find(u => u.id === option?.value);
            if (!user) return false;
            return (
              user.fullName.toLowerCase().includes(input.toLowerCase()) ||
              user.email.toLowerCase().includes(input.toLowerCase()) ||
              user.department?.toLowerCase().includes(input.toLowerCase())
            );
          }}
        >
          {availableUsers?.map(user => (
            <Select.Option key={user.id} value={user.id}>
              {user.fullName} ({user.email})
            </Select.Option>
          ))}
        </Select>
      </Modal>
    </div>
  );
}
